import { useEffect, useMemo } from 'react'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Doughnut } from 'react-chartjs-2'

import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  GetPatientStatsQuery,
} from '@services/patientStatsService'
import {
  resetPatientStatsFilters,
  setPatientStatsFilters,
} from '@state/reducers/statsReducer'
import { getPatientStats, getPatientStatsOptions } from '@state/thunks/statsThunk'

ChartJS.register(ArcElement, Tooltip, Legend)

const genderChartColors = ['#36A2EB', '#FF6384', '#9E9E9E']

const getCareTypeLabel = (careType: string) => {
  switch (careType) {
    case 'OUTPATIENT':
      return 'Externe'
    case 'INPATIENT':
      return 'Hospitalisé'
    case 'AMBULATORY':
      return 'Ambulatoire'
    case 'UNKNOWN':
      return 'Inconnu'
    default:
      return careType
  }
}

const getGenderLabel = (gender: string) => {
  switch (gender) {
    case 'male':
      return 'Homme'
    case 'female':
      return 'Femme'
    default:
      return gender
  }
}

const formatAgeValue = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '-'
  }

  return `${value.toFixed(1)} ans`
}

export default function PatientStats() {
  const dispatch = useAppDispatch()
  const {
    filters,
    sites,
    total,
    departments,
    municipalities,
    careTypes,
    genders,
    ageSummary,
    loading,
    loadingOptions,
    error,
    hasLoaded,
    hasLoadedOptions,
  } = useAppSelector(({ stats }) => stats.patientStats)

  useEffect(() => {
    if (!hasLoadedOptions) {
      void dispatch(getPatientStatsOptions())
    }

    if (!hasLoaded) {
      void dispatch(getPatientStats())
    }
  }, [dispatch, hasLoaded, hasLoadedOptions])

  const sortedDepartments = useMemo(
    () => [...departments].sort((left, right) => right.total - left.total),
    [departments],
  )

  const sortedMunicipalities = useMemo(
    () => [...municipalities].sort((left, right) => right.total - left.total),
    [municipalities],
  )

  const sortedCareTypes = useMemo(
    () => [...careTypes].sort((left, right) => right.total - left.total),
    [careTypes],
  )

  const sortedGenders = useMemo(
    () => [...genders].sort((left, right) => right.total - left.total),
    [genders],
  )

  const genderChartData = useMemo(
    () => ({
      labels: sortedGenders.map((item) => getGenderLabel(item.gender)),
      datasets: [
        {
          data: sortedGenders.map((item) => item.total),
          backgroundColor: sortedGenders.map(
            (_, index) => genderChartColors[index % genderChartColors.length],
          ),
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }),
    [sortedGenders],
  )

  const handleFilterChange = (
    key: keyof GetPatientStatsQuery,
    value: string | number | undefined,
  ) => {
    dispatch(
      setPatientStatsFilters({
        [key]: value,
      }),
    )
  }

  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    handleFilterChange('siteId', value === '' ? undefined : Number(value))
  }

  const handleTextFilterChange = (
    key: 'departmentCode' | 'zipCode',
    value: string,
  ) => {
    const normalizedValue = value.trim()
    handleFilterChange(key, normalizedValue === '' ? undefined : normalizedValue)
  }

  const handleApplyFilters = () => {
    void dispatch(getPatientStats())
  }

  const handleResetFilters = () => {
    dispatch(resetPatientStatsFilters())
    void dispatch(getPatientStats())
  }

  return (
    <Box sx={{ padding: 3, paddingRight: 2 }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            position: 'sticky',
            top: 16,
            zIndex: 10,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Statistiques des patients
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="patient-stats-site-label">Site</InputLabel>
                    <Select
                      labelId="patient-stats-site-label"
                      value={filters.siteId?.toString() ?? ''}
                      label="Site"
                      onChange={handleSiteChange}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">Tous les sites</MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.id} value={site.id.toString()}>
                          {site.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Département"
                    value={filters.departmentCode ?? ''}
                    onChange={(event) =>
                      handleTextFilterChange(
                        'departmentCode',
                        event.target.value.toUpperCase(),
                      )
                    }
                    placeholder="Ex. 75, 13, 2A"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Code postal"
                    value={filters.zipCode ?? ''}
                    onChange={(event) =>
                      handleTextFilterChange('zipCode', event.target.value)
                    }
                    placeholder="Ex. 75001"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de début"
                    value={filters.startDate}
                    onChange={(event) => handleFilterChange('startDate', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de fin"
                    value={filters.endDate}
                    onChange={(event) => handleFilterChange('endDate', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
                    Appliquer
                  </Button>
                  <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
                    Réinitialiser
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total des patients
              </Typography>
              <Typography variant="h3">{total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre pour la période et les filtres sélectionnés.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Types de prise en charge
              </Typography>
              <Typography variant="h3">{sortedCareTypes.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre de types distincts renvoyés pour la période sélectionnée.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Départements
              </Typography>
              <Typography variant="h3">{sortedDepartments.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre de départements distincts présents dans les résultats.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Communes
              </Typography>
              <Typography variant="h3">{sortedMunicipalities.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre de communes distinctes présentes dans les résultats.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Âge moyen
              </Typography>
              <Typography variant="h3">{formatAgeValue(ageSummary?.meanAge)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Moyenne d&apos;âge des patients correspondant aux filtres.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Âge médian
              </Typography>
              <Typography variant="h3">{formatAgeValue(ageSummary?.medianAge)}</Typography>
              <Typography variant="body2" color="text.secondary">
                Médiane d&apos;âge des patients correspondant aux filtres.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Origine par département</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Département</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && sortedDepartments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          Aucune donnée trouvée pour les filtres sélectionnés.
                        </TableCell>
                      </TableRow>
                    )}

                    {sortedDepartments.map((item) => (
                      <TableRow key={item.departmentCode}>
                        <TableCell>{item.departmentCode}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Origine par commune</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code postal</TableCell>
                      <TableCell>Ville</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && sortedMunicipalities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Aucune donnée trouvée pour les filtres sélectionnés.
                        </TableCell>
                      </TableRow>
                    )}

                    {sortedMunicipalities.map((item) => (
                      <TableRow key={`${item.zipCode}-${item.city}`}>
                        <TableCell>{item.zipCode}</TableCell>
                        <TableCell>{item.city}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par sexe</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              {genderChartData.labels.length === 0 && !loading ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée trouvée pour les filtres sélectionnés.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ height: 280, marginBottom: 2 }}>
                    <Doughnut
                      data={genderChartData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gap: 1 }}>
                    {sortedGenders.map((item, index) => (
                      <Box
                        key={item.gender}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: 1.5,
                          borderRadius: 2,
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor:
                                genderChartColors[index % genderChartColors.length],
                            }}
                          />
                          <Typography>{getGenderLabel(item.gender)}</Typography>
                        </Box>
                        <Typography color="text.secondary">
                          {item.total} ({item.percentage.toFixed(2)} %)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par type de prise en charge</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Pourcentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && sortedCareTypes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Aucune donnée trouvée pour les filtres sélectionnés.
                        </TableCell>
                      </TableRow>
                    )}

                    {sortedCareTypes.map((item) => (
                      <TableRow key={item.careType}>
                        <TableCell>{getCareTypeLabel(item.careType)}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                        <TableCell align="right">{item.percentage.toFixed(2)} %</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
